// See note in `../Modal`, only this component should be allowed to import Modal from reefer.
// eslint-disable-next-line no-restricted-imports
import { Button, Flex, Modal, Typography } from '@iheartjane/reefer';
import { useCallback, useEffect, useState } from 'react';

import { useGetCart } from '../../../data-access';
import { useKioskSpacing } from '../../../hooks';
import { useIdleContext } from '../../../providers';
import { trackTimeoutDialogTap } from '../../../util';

interface IdlePromptProps {
  isDeletingCart: boolean;
  isOpen: boolean;
  onContinue: () => void;
  onReset: () => void;
}

export const IdlePrompt = ({
  isDeletingCart,
  isOpen,
  onContinue,
  onReset,
}: IdlePromptProps) => {
  const { data: cart } = useGetCart();

  const { getRemainingTime, timeoutMs } = useIdleContext();

  const [remaining, setRemaining] = useState<number>(
    Math.ceil(getRemainingTime() / 1000)
  );

  const { defaultSpacing, getKioskSpacing, isIpad } = useKioskSpacing();

  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining(Math.ceil(getRemainingTime() / 1000));
    }, 500);

    return () => {
      clearInterval(interval);
    };
  }, [getRemainingTime]);

  const onStartOver = useCallback(() => {
    trackTimeoutDialogTap({
      selectedOption: 'start over',
      cart,
      timeoutMs,
    });
    onReset();
  }, [cart, onReset, timeoutMs]);

  const onResume = useCallback(() => {
    trackTimeoutDialogTap({
      selectedOption: "i'm still here",
      cart,
      timeoutMs,
    });
    onContinue();
  }, [cart, timeoutMs, onContinue]);

  return (
    <Modal
      appId="root"
      contentLabel="Idle prompt"
      onRequestClose={onResume}
      open={isOpen}
      overflow="visible"
      variant="dialogue"
    >
      <Modal.Content padding={false}>
        <Flex flexDirection="column" p={getKioskSpacing(32, 40)}>
          <Typography variant="header-bold">Are you still there?</Typography>
          <Typography mb={getKioskSpacing(24, 8)} variant="header-bold">
            This kiosk will reset in {remaining.toString()}
          </Typography>
          <Typography mb={defaultSpacing}>
            To make sure other customers are able to shop, this session will
            automatically reset after there hasn't been any activity for a
            while.
          </Typography>
          <Flex flexDirection="row">
            <Button
              ariaLabel="restart session"
              disabled={isDeletingCart}
              full
              label={
                <Typography
                  color="text-main"
                  variant={`${isIpad ? 'body' : 'section'}-bold`}
                >
                  Start over
                </Typography>
              }
              loading={isDeletingCart}
              mr={24}
              onClick={onStartOver}
              variant="tertiary"
              size={isIpad ? 'default' : 'large'}
            />
            <Button
              ariaLabel="reactivate session"
              disabled={isDeletingCart}
              full
              label={
                <Typography
                  color="text-info"
                  variant={`${isIpad ? 'body' : 'section'}-bold`}
                >
                  I'm still here
                </Typography>
              }
              onClick={onResume}
              variant="primary"
              size={isIpad ? 'default' : 'large'}
            />
          </Flex>
        </Flex>
      </Modal.Content>
    </Modal>
  );
};
